import React from 'react';

import classnames from 'classnames/bind';

import { AdminUser, useGetAllUsers } from '@SERVICES';

import useGlobalPermissions from '@HOOKS/useGlobalPermissions';
import useDeleteUserModal from '@HOOKS/store/modals/useDeleteUserModal';
import useAuth from '@HOOKS/useAuth';

import RoundIconButton from '@COMPONENTS/COMMON/buttons/RoundIconButton';

import styles from './UserHeader.module.scss';

const cx: CX = classnames.bind(styles);

function UserHeader(props: Props) {
    const { user } = props;

    const {
        canManagePermissions,
    } = useGlobalPermissions();

    const {
        actions: deleteUserActions,
    } = useDeleteUserModal();

    const { state: { user: currentUser } } = useAuth();
    const { data: usersData = { data: [] } } = useGetAllUsers();

    // Get current user's full data including isSuperAdmin and canAccessAppSettings
    const currentUserFullData = usersData.data.find((u) => u.id === currentUser?.id);

    // Determine if current user can see super admin badges (only super admins can see them)
    const canSeeSuperAdminBadges = currentUserFullData?.isSuperAdmin || false;

    // Determine if current user can see app settings access badges (only admins or super admins can see them)
    const canSeeAppSettingsBadges = currentUserFullData?.isSuperuser || currentUserFullData?.isSuperAdmin || false;

    const getUserStatusBadge = () => {
        if (!user.isActive) {
            return <span className={cx('status-badge', 'inactive')}>Inactive</span>;
        }
        if (user.isSuperuser || user.isSuperAdmin) {
            return <span className={cx('status-badge', 'admin')}>Admin</span>;
        }
        return <span className={cx('status-badge', 'active')}>Active</span>;
    };

    return (
        <div className={cx('user-header')}>
            <div className={cx('user-main-info')}>
                <div className={cx('user-name')}>
                    {user.fullName}
                </div>
                <div className={cx('user-email')}>
                    {user.email}
                </div>
                {user.title && (
                    <div className={cx('user-title')}>
                        {user.title}
                        {' • '}
                        {user.userType}
                    </div>
                )}
                <div className={cx('user-status')}>
                    {getUserStatusBadge()}
                    {canSeeAppSettingsBadges && user.canAccessAppSettings && (
                        <span className={cx('permission-badge')}>App Settings</span>
                    )}
                    {canSeeSuperAdminBadges && user.isSuperAdmin && (
                        <span className={cx('permission-badge', 'super')}>Super Admin</span>
                    )}
                </div>
            </div>

            <div className={cx('user-actions')}>
                <RoundIconButton
                    locked={!canManagePermissions}
                    tooltipTitle="Delete user"
                    icon="delete"
                    onClick={() => {
                        deleteUserActions.open(user);
                    }}
                />
            </div>
        </div>
    );
}

type Props = {
    user: AdminUser;
};

export default UserHeader; 